import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { numberLocale } from '@/lib/language';
import { SUPPORT_EMAIL, isCapTier, planTierLabel } from '@/lib/plans';

interface SpecialPlanCardProps {
  tier: string;
  productLimit: number;
}

/**
 * Karte fuer Sondertarife (D7, UX-Audit K37): individueller Tarif mit
 * Kapazitaet und Kontakt. Band-Karten darunter sind nur Information.
 * Free und Baender mit erhoehtem Limit tragen keinen eigenen Namen: "Free"
 * oder "500 Produkte" im Titel waere bei 30.000 betreuten Produkten falsch.
 */
export const SpecialPlanCard: React.FC<SpecialPlanCardProps> = ({ tier, productLimit }) => {
  const { t, i18n } = useTranslation('pricing');
  const language = i18n?.language;
  const hasOwnName = tier !== 'free' && !isCapTier(tier);
  const title = hasOwnName
    ? t('specialPlan.title', { plan: planTierLabel(tier, language, t) })
    : t('specialPlan.titleNoName');

  return (
    <Card data-testid="special-plan-card" className="border-primary/50 bg-primary/5">
      <CardHeader className="space-y-2">
        <Badge variant="secondary" className="w-fit">{t('specialPlan.badge')}</Badge>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          {t('specialPlan.capacity', { limit: productLimit.toLocaleString(numberLocale(language)) })}
        </p>
        <p className="text-sm text-muted-foreground">{t('specialPlan.note')}</p>
        <Button asChild variant="outline" className="gap-2">
          <a href={`mailto:${SUPPORT_EMAIL}`}>
            <Mail className="h-4 w-4" aria-hidden="true" />
            {t('specialPlan.contact')}
          </a>
        </Button>
      </CardContent>
    </Card>
  );
};
